import { formatMoney } from '@/i18n';
import { getUiLocale, type UiLocale } from '@/i18n/locale';

/**
 * billing-display.ts — формат суммы тарифа для показа.
 *
 * WEB-4180 (условие сессии 52 03.11): вторая копия тарифов кабинета (таблица
 * `BILLING_TIERS_DISPLAY` с лимитами FREE / PRO, порядок тарифов и подписи
 * лимитов окна выбора тарифа) снята вместе с окном `PricingModal`: тариф один,
 * все суммы и условия — из `planTerms` ответа `GET /billing`. Здесь остался
 * только общий формат денег.
 */
/**
 * WEB-611 (money HIGH): formats a price in cents fetched live from Stripe
 * (`GET /billing/plans`). Used for PRO, which no longer carries a
 * static `monthlyPriceCents` — see the file-level doc above.
 *
 * WEB-665 (round 2): locale is pinned to English, not `undefined` — mirrors
 * the identical fix already applied in utils/formatCents.ts. With
 * `undefined`, Intl resolves the VIEWER's own browser locale; for any
 * non-US-English locale (seen in the coordinator's Chrome), USD renders
 * disambiguated as "US$29" instead of the canon-mockup "$29". Pinning
 * English makes the "$"-only format deterministic everywhere.
 *
 * WEB-1874 менял здесь только ДВЕРЬ (строка локали живёт в i18n/format.ts) —
 * пин, делитель и число знаков после запятой прежние, до цента.
 *
 * WEB-4180 (решение владельца 03.11): знак «$» впереди остаётся по пину `en`,
 * а разряды — по языку кабинета: ru — неразрывный пробел («$2 388», сумма не
 * переносится), en — запятая («$2,388»). Язык — текущий язык кабинета или
 * явный последним параметром.
 */
export function formatPriceCents(
  cents: number,
  currency: string,
  locale: UiLocale = getUiLocale(),
): string {
  const en = formatMoney(
    cents / 100,
    currency,
    { minimumFractionDigits: 0, maximumFractionDigits: 0 },
    'en',
  );
  return locale === 'ru' ? en.replace(/,/g, '\u00a0') : en;
}
